/* SIWS CUSTOM: 250th Anniversary 20 oz Tumbler Picker — tumbler-picker.js
   Per-color steppers (Black/White/Navy, step=1), 6-unit total minimum, live "Adding to Cart"
   summary, progress tracker. Mirrors assets/screwdriver-picker.js mechanism with these diffs:
     - 3 colors instead of 12, step=1 instead of 50, total minimum 6 instead of 150
     - Whole-batch Back toggle (Blank $23 / Dealer Info $27) drives unit price + dealer-fields lockout
     - _addToCart posts ONE line item PER color with qty>0, each to the (color × back) variant ID
   Initialized on DOMContentLoaded for all .tumbler-picker elements. */

'use strict';

// SIWS CUSTOM: 3 colors. hex values used for swatch + summary dot.
const SIWS_TUMBLER_COLORS = [
  { id: 'black', name: 'Black', hex: '#212121' },
  { id: 'white', name: 'White', hex: '#FFFFFF' },
  { id: 'navy',  name: 'Navy',  hex: '#0B2D5E' },
];

// SIWS CUSTOM: Order constraints. Display-only prices in dollars; cart uses real variant prices.
const SIWS_TUMBLER_MIN_TOTAL   = 6;
const SIWS_TUMBLER_PRICE_BLANK = 23.00;
const SIWS_TUMBLER_PRICE_INFO  = 27.00;

class SIWSTumblerPicker {
  constructor(el) {
    this.el            = el;
    this.productTitle  = el.dataset.productTitle  || '250th Anniversary 20 oz Tumbler';
    this.customerName  = el.dataset.customerName  || '';
    this.customerEmail = el.dataset.customerEmail || '';

    this.colorQtys    = {};   // colorId → integer qty
    this.selectedBack = null; // 'blank' | 'info'

    this.init();
  }

  init() {
    this._renderColorCards();
    this._bindEvents();
    this._toggleDealerFields();
    this._update();
  }

  // ── Queries ──────────────────────────────────────────────────

  _totalQty() {
    return Object.values(this.colorQtys).reduce((s, q) => s + q, 0);
  }

  _selectedColors() {
    return SIWS_TUMBLER_COLORS.filter(c => (this.colorQtys[c.id] || 0) > 0);
  }

  _unitPrice() {
    return this.selectedBack === 'info'
      ? SIWS_TUMBLER_PRICE_INFO
      : SIWS_TUMBLER_PRICE_BLANK;
  }

  _dealerName() {
    const el = this.el.querySelector('#tumbler-dealer-name');
    return el ? el.value.trim() : '';
  }

  _phone() {
    const el = this.el.querySelector('#tumbler-phone');
    return el ? el.value.trim() : '';
  }

  // SIWS CUSTOM: Resolve cart variant for a given color by reading data-variant-{color}-{back}.
  _resolveVariantId(colorId) {
    if (!this.selectedBack) return null;
    const key = `variant${_siwsCap(colorId)}${_siwsCap(this.selectedBack)}`;
    return this.el.dataset[key] || null;
  }

  _isValid() {
    if (!this.selectedBack) return false;
    if (this._totalQty() < SIWS_TUMBLER_MIN_TOTAL) return false;
    if (this.selectedBack === 'info') {
      if (!this._dealerName() || !this._phone()) return false;
    }
    // Every selected color must resolve to a real variant ID.
    return this._selectedColors().every(c => this._resolveVariantId(c.id) !== null);
  }

  _validationMessage() {
    if (!this.selectedBack) return 'Select Blank or Dealer Info';
    const total = this._totalQty();
    if (total < SIWS_TUMBLER_MIN_TOTAL) {
      if (total === 0) return `Minimum ${SIWS_TUMBLER_MIN_TOTAL} tumblers required`;
      const remaining = SIWS_TUMBLER_MIN_TOTAL - total;
      return `${remaining} more tumbler${remaining === 1 ? '' : 's'} needed (${total} of ${SIWS_TUMBLER_MIN_TOTAL})`;
    }
    if (this.selectedBack === 'info') {
      if (!this._dealerName()) return 'Dealer / Business Name is required';
      if (!this._phone())      return 'Phone number is required';
    }
    return '';
  }

  // ── Qty mutations ────────────────────────────────────────────

  _increment(id) {
    this.colorQtys[id] = (this.colorQtys[id] || 0) + 1;
    this._update();
  }

  _decrement(id) {
    const current = this.colorQtys[id] || 0;
    if (current <= 0) return;
    this.colorQtys[id] = current - 1;
    this._update();
  }

  // ── Card rendering ───────────────────────────────────────────

  _renderColorCards() {
    const container = this.el.querySelector('#tumbler-color-cards');
    if (!container) {
      console.error('[SIWS] _renderColorCards: #tumbler-color-cards not found');
      return;
    }
    const grid = document.createElement('div');
    grid.className = 'tumbler-color-grid';
    SIWS_TUMBLER_COLORS.forEach(color => grid.appendChild(this._buildCardEl(color)));
    container.appendChild(grid);
  }

  _buildCardEl(color) {
    const card = document.createElement('div');
    card.className       = 'tumbler-color-card';
    card.dataset.colorId = color.id;

    const swatchWrap = document.createElement('div');
    swatchWrap.className = 'tumbler-color-card__swatch-wrap';
    const swatch = document.createElement('span');
    swatch.className             = 'tumbler-color-card__swatch';
    swatch.style.backgroundColor = color.hex;
    if (color.id === 'white') swatch.style.border = '2px solid rgba(0,0,0,0.15)';
    swatch.setAttribute('aria-hidden', 'true');
    swatchWrap.appendChild(swatch);

    const name = document.createElement('span');
    name.className   = 'tumbler-color-card__name';
    name.textContent = color.name;

    const stepper = document.createElement('div');
    stepper.className = 'tumbler-color-card__stepper';

    const minus = document.createElement('button');
    minus.type               = 'button';
    minus.className          = 'tumbler-color-card__minus';
    minus.dataset.colorId    = color.id;
    minus.textContent        = '−';
    minus.disabled           = true;
    minus.setAttribute('aria-label', `Remove one ${color.name}`);

    const qty = document.createElement('span');
    qty.className   = 'tumbler-color-card__qty';
    qty.textContent = '0';

    const plus = document.createElement('button');
    plus.type             = 'button';
    plus.className        = 'tumbler-color-card__plus';
    plus.dataset.colorId  = color.id;
    plus.textContent      = '+';
    plus.setAttribute('aria-label', `Add one ${color.name}`);

    stepper.appendChild(minus);
    stepper.appendChild(qty);
    stepper.appendChild(plus);

    card.appendChild(swatchWrap);
    card.appendChild(name);
    card.appendChild(stepper);

    return card;
  }

  // ── Event binding ────────────────────────────────────────────

  _bindEvents() {
    // Stepper buttons — delegated on .tumbler-picker.
    this.el.addEventListener('click', e => {
      const plus  = e.target.closest('.tumbler-color-card__plus');
      const minus = e.target.closest('.tumbler-color-card__minus');
      if (plus)  this._increment(plus.dataset.colorId);
      if (minus) this._decrement(minus.dataset.colorId);
    });

    // Back toggle.
    this.el.querySelectorAll('.tumbler-option[data-back]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.selectedBack = btn.dataset.back;
        this._toggleDealerFields();
        this._update();
      });
    });

    // Dealer-info inputs (re-validate on every keystroke / change).
    const nameEl  = this.el.querySelector('#tumbler-dealer-name');
    const phoneEl = this.el.querySelector('#tumbler-phone');
    if (nameEl)  nameEl.addEventListener('input', () => this._update());
    if (phoneEl) {
      phoneEl.addEventListener('input', () => this._update());
      // SIWS CUSTOM: Phone format on blur — mirrors assets/gift-card-picker.js:25-29,41-44.
      // 10 digits → "(XXX) XXX-XXXX"; non-10-digit input left as-is so dealers can keep
      // extensions or other notations.
      phoneEl.addEventListener('blur', () => {
        phoneEl.value = _siwsFormatPhone(phoneEl.value);
        this._update();
      });
    }

    // Add to Cart.
    const atcBtn = this.el.querySelector('#tumbler-atc-btn');
    if (atcBtn) atcBtn.addEventListener('click', () => this._addToCart());

    // SIWS CUSTOM: Contact-us link carries current picker state into ?body= on /pages/contact.
    // contact-form.liquid pre-fills #ContactForm-body from that param on DOMContentLoaded.
    const contactLink = this.el.querySelector('#tumbler-contact-link');
    if (contactLink) {
      contactLink.addEventListener('click', e => {
        try {
          e.preventDefault();
          const body = this._buildContactBody();
          window.location.href = '/pages/contact?body=' + encodeURIComponent(body);
        } catch (err) {
          // Fall back to plain contact page if state can't be read.
          console.error('[SIWS] Tumbler contact link error:', err);
          window.location.href = '/pages/contact';
        }
      });
    }
  }

  // SIWS CUSTOM: Compose the contact-form body from current picker state.
  // "Custom engraved tumbler inquiry. Current selection: 3 Black, 3 Navy. Back: Dealer Info."
  // If no colors selected and no back chosen, just the generic opening line.
  _buildContactBody() {
    const lead = 'Custom engraved tumbler inquiry.';
    const selected = this._selectedColors();
    if (selected.length === 0 && !this.selectedBack) return lead;

    const parts = [lead];
    if (selected.length > 0) {
      const list = selected.map(c => `${this.colorQtys[c.id]} ${c.name}`).join(', ');
      parts.push(`Current selection: ${list}.`);
    }
    if (this.selectedBack) {
      const lbl = this.selectedBack === 'info' ? 'Dealer Info' : 'Blank';
      parts.push(`Back: ${lbl}.`);
    }
    return parts.join(' ');
  }

  // ── Coordinated update ───────────────────────────────────────

  _update() {
    this._updateBackStates();
    this._updateCounter();
    this._updateCardStates();
    this._updateSummary();
    this._updateButton();
  }

  _updateBackStates() {
    this.el.querySelectorAll('.tumbler-option[data-back]').forEach(btn => {
      btn.classList.toggle('tumbler-option--selected', btn.dataset.back === this.selectedBack);
    });
  }

  _updateCounter() {
    const total     = this._totalQty();
    const met       = total >= SIWS_TUMBLER_MIN_TOTAL;
    const unitPrice = this._unitPrice();
    const cost      = (total * unitPrice).toFixed(2);
    const pct       = Math.min(100, (total / SIWS_TUMBLER_MIN_TOTAL) * 100);

    const countText = this.el.querySelector('#tumbler-count-text');
    const priceText = this.el.querySelector('#tumbler-price-text');
    const fill      = this.el.querySelector('#tumbler-progress-fill');
    const counter   = this.el.querySelector('#tumbler-counter');

    if (countText) {
      countText.textContent = met
        ? `${total} tumblers — minimum met ✓`
        : `${total} of ${SIWS_TUMBLER_MIN_TOTAL} minimum tumblers`;
    }
    if (priceText) priceText.textContent = total > 0 ? ` · $${cost}` : '';
    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('tumbler-progress__fill--complete', met);
    }
    if (counter) counter.classList.toggle('tumbler-counter--complete', met);
  }

  _updateCardStates() {
    this.el.querySelectorAll('.tumbler-color-card').forEach(card => {
      const id  = card.dataset.colorId;
      const qty = this.colorQtys[id] || 0;
      card.classList.toggle('tumbler-color-card--selected', qty > 0);

      const plus  = card.querySelector('.tumbler-color-card__plus');
      const minus = card.querySelector('.tumbler-color-card__minus');
      const qtyEl = card.querySelector('.tumbler-color-card__qty');

      if (qtyEl) qtyEl.textContent = qty;
      if (minus) minus.disabled    = qty === 0;
      if (plus)  plus.disabled     = false;
    });
  }

  _updateSummary() {
    const summaryEl = this.el.querySelector('#tumbler-summary');
    if (!summaryEl) return;

    const selected = this._selectedColors();
    if (selected.length === 0 || !this.selectedBack) {
      summaryEl.hidden = true;
      return;
    }

    const unitPrice = this._unitPrice();
    const total     = this._totalQty();
    const totalCost = (total * unitPrice).toFixed(2);
    const backLabel = this.selectedBack === 'info' ? 'Dealer Info' : 'Blank';

    const rows = selected.map(c => {
      const qty      = this.colorQtys[c.id];
      const lineCost = (qty * unitPrice).toFixed(2);
      const border   = c.id === 'white' ? 'border:1px solid #ccc;' : '';
      return `<tr>
        <td>
          <span class="tumbler-summary__dot" style="background:${c.hex};${border}"></span>
          ${c.name}
        </td>
        <td class="tumbler-summary__qty-cell">${qty} × $${unitPrice.toFixed(2)} = $${lineCost}</td>
      </tr>`;
    }).join('');

    summaryEl.innerHTML = `
      <h4 class="tumbler-summary__title">Adding to Cart — ${backLabel}</h4>
      <table class="tumbler-summary__table">
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="tumbler-summary__total-row">
            <td>Total</td>
            <td class="tumbler-summary__qty-cell">${total} tumblers · $${totalCost}</td>
          </tr>
        </tfoot>
      </table>
    `;
    summaryEl.hidden = false;
  }

  _updateButton() {
    const btn = this.el.querySelector('#tumbler-atc-btn');
    if (!btn) return;

    const valid = this._isValid();
    btn.disabled = !valid;

    if (valid) {
      const total = this._totalQty();
      const cost  = (total * this._unitPrice()).toFixed(2);
      btn.textContent = `Add ${total} Tumblers to Cart — $${cost}`;
    } else {
      btn.textContent = this._validationMessage() || `Minimum ${SIWS_TUMBLER_MIN_TOTAL} tumblers required`;
    }
  }

  // SIWS CUSTOM: Dealer-info lockout — box stays visible, but all inputs are .disabled and the
  // wrapper carries .tumbler-dealer-fields--disabled (visual dim) unless Back = Dealer Info.
  _toggleDealerFields() {
    const fields = this.el.querySelector('#tumbler-dealer-fields');
    if (!fields) return;
    const enabled = this.selectedBack === 'info';
    fields.classList.toggle('tumbler-dealer-fields--disabled', !enabled);
    fields.querySelectorAll('input').forEach(input => {
      input.disabled = !enabled;
    });
  }

  // ── Shared helpers ───────────────────────────────────────────

  _cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/';
  }

  _hideError() {
    const el = this.el.querySelector('#tumbler-atc-error');
    if (el) el.hidden = true;
  }

  _showError(message) {
    const el = this.el.querySelector('#tumbler-atc-error');
    if (!el) return;
    el.textContent = message;
    el.hidden      = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ── Add to Cart ──────────────────────────────────────────────

  async _addToCart() {
    const btn = this.el.querySelector('#tumbler-atc-btn');
    if (!btn || btn.disabled) return;

    const selected = this._selectedColors();
    if (selected.length === 0) {
      this._showError('Add at least one tumbler.');
      return;
    }

    // SIWS CUSTOM: Build per-line properties — branched on selectedBack.
    // 'Back' is NOT included as a line-item property: Shopify already renders
    // the variant's option2 (Blank / Dealer Info) in the cart drawer, so a
    // 'Back: Blank' property would appear duplicated alongside the variant
    // title. Dealer-info case keeps Name + Phone as properties because those
    // aren't part of the variant.
    let properties;
    if (this.selectedBack === 'info') {
      properties = {
        'Dealer Name':  this._dealerName(),
        'Phone Number': this._phone(),
      };
    } else {
      properties = {};
    }

    // SIWS CUSTOM: One line item per color, each at its own (color × back) variant ID.
    const items = selected.map(c => ({
      id:         this._resolveVariantId(c.id),
      quantity:   this.colorQtys[c.id],
      properties: properties,
    }));

    // Defensive: refuse if any variant ID failed to resolve.
    if (items.some(it => !it.id)) {
      this._showError('Could not resolve product variants. Please refresh and try again.');
      return;
    }

    const originalText = btn.textContent;
    btn.disabled    = true;
    btn.textContent = 'Adding to cart…';
    this._hideError();

    try {
      // SIWS CUSTOM: Stay-on-page ATC — mirrors assets/calendars-picker.js:303-362.
      // Augment the /cart/add.js body with Section Rendering API hints so the Dawn
      // cart drawer/notification can re-render itself from the response. Falls back
      // to a cart-bubble refresh when no cart element is present (page-mode).
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = {
        items,
        sections:     cart ? cart.getSectionsToRender().map(s => s.id) : ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      const res = await fetch(`${this._cartRoot()}cart/add.js`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add tumblers to cart.');
      }

      const payload = await res.clone().json().catch(() => ({}));

      // Notify cart.js enforcers + drawer.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-tumbler', cartData: payload });
      }

      if (cart) {
        cart.renderContents(payload); // opens the drawer
      } else {
        // Page-mode fallback: refresh the cart bubble without leaving the PDP.
        const newBubbleHTML = payload.sections && payload.sections['cart-icon-bubble'];
        const liveBubble    = document.getElementById('cart-icon-bubble');
        if (newBubbleHTML && liveBubble) {
          const parsed = new DOMParser().parseFromString(newBubbleHTML, 'text/html');
          const inner  = parsed.querySelector('.shopify-section');
          if (inner) liveBubble.innerHTML = inner.innerHTML;
        }
      }

      // Reset picker state so a re-click can't double-add the same batch.
      this.colorQtys = {};
      this._update();
      btn.disabled    = true;
      btn.textContent = `Minimum ${SIWS_TUMBLER_MIN_TOTAL} tumblers required`;

    } catch (err) {
      console.error('[SIWS] Tumbler add-to-cart error:', err);
      btn.disabled    = false;
      btn.textContent = originalText;
      this._showError(err.message || 'Something went wrong. Please try again.');
    }
  }
}

// 'black' → 'Black', 'dealer-info' → 'DealerInfo'.
function _siwsCap(s) {
  return s.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

// SIWS CUSTOM: 10 digits → "(XXX) XXX-XXXX". Copied from assets/gift-card-picker.js:25-29.
function _siwsFormatPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length !== 10) return String(raw || '').trim();
  return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
}

// SIWS CUSTOM: Initialize SIWSTumblerPicker for every .tumbler-picker on the page.
// readyState guard handles deferred-script timing edge cases.
function _siwsInitTumblerPickers() {
  document.querySelectorAll('.tumbler-picker').forEach(el => {
    new SIWSTumblerPicker(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _siwsInitTumblerPickers);
} else {
  _siwsInitTumblerPickers();
}
